/**
 * Walking a record forward, one version at a time.
 *
 * The chain is a list of single-version steps, and this walks it: find the step that reads the
 * version in hand, apply it, repeat until the record is current. A version with no step is a hole
 * somebody has to fill, and it fails by name rather than by loading a record no step has touched.
 *
 * A record from a newer build never reaches a step. There is no way to walk backwards and no way to
 * guess what a field this build has never seen means, so it is refused before anything reads it and
 * `profileStore` never writes over it. Losing a character to a browser that had two tabs on two
 * builds is exactly the failure this repo treats as a release blocker.
 *
 * The chain is a parameter rather than an import, so the tests exercise the walk against a fixture
 * chain instead of waiting for the schema to change to have something to migrate.
 */

import { refused, saved } from '@/save/saveResult';
import type { SaveMigration, SaveRecord } from '@/save/migrationContract';
import type { SaveResult } from '@/save/saveResult';

export function runMigrations(
  record: SaveRecord,
  from: number,
  target: number,
  chain: readonly SaveMigration[],
): SaveResult<SaveRecord> {
  if (!Number.isInteger(from)) return refused({ reason: 'malformed', field: 'schemaVersion' });
  if (from > target) return refused({ reason: 'fromNewerBuild', found: from, current: target });

  let current = record;

  for (let version = from; version < target; version += 1) {
    const step = chain.find((migration) => migration.from === version);
    if (step === undefined) {
      return refused({ reason: 'noMigrationPath', found: from, missingFrom: version });
    }
    current = { ...step.apply(current), schemaVersion: version + 1 };
  }

  return saved(current);
}
